import { useInfiniteQuery } from '@tanstack/react-query'
import useAxios from 'api/useAxios'
import {
  flattenPaginatedData,
  queryBuilder,
  recursiveCamelCase,
} from 'api/helper'
import { IModel } from 'interfaces/model.interface'
import { IBaseView } from 'interfaces/navigationPage.interface'
import { formatSort } from '..'
import { ICleanResponse } from 'api/types'
import { getNextPageParam } from 'api/useCollectionData'
import { type ArticleFiltersT } from 'utils/useArticleFilters'
import useTracking, { TrackingTypes } from 'tracking/useTracking'

function getFlattenFilters(filters: ArticleFiltersT) {
  const flattenedFilters = {
    selectedDiseases: filters?.diseases.map((item: any) => item.value),
    selectedOrgs: filters?.orgs.map((item: any) => item.value),
    selectedCandidates: filters?.candidates.map((item: any) => item.value),
    selectedLocations: filters?.locations.map((item: any) => item.value),
    selectedPathogenTypes: filters?.pathogenTypes.map(
      (item: any) => item.value
    ),
    selectedTechTypes: filters?.techTypes.map((item: any) => item.value),
    selectedTags: filters?.tags.map((item: any) => item.value),
  }
  return flattenedFilters as Record<string, string[]>
}

function trackFilters(filters: ArticleFiltersT, tracking: TrackingTypes) {
  const filterLabels = Object.fromEntries(
    Object.entries({
      diseases: filters?.diseases.map((item: any) => item.label),
      locations: filters?.locations.map((item: any) => item.label),
      orgs: filters?.orgs.map((item: any) => item.label),
      candidates: filters?.candidates.map((item: any) => item.label),
      pathogenTypes: filters?.pathogenTypes.map((item: any) => item.label),
    }).filter(([, value]) => value.length > 0)
  )
  if (Object.keys(filterLabels).length > 0) {
    tracking.articlesFilters({ filter: filterLabels })
  }
}

export function useFilteredArticles({
  filters,
  view,
  model,
  maxItems = 10,
  disableTracking,
}: {
  filters: ArticleFiltersT
  view: IBaseView<any, any>
  model: IModel<any>
  maxItems?: number
  disableTracking?: boolean
}) {
  const [tracking] = useTracking()
  const axios = useAxios()
  const flattenedFilters = getFlattenFilters(filters)
  const sort =
    model.defaultSortObject && model.defaultSortObject.length > 0
      ? formatSort(
          model.defaultSortObject[0].id,
          model.defaultSortObject[0]?.sortOrder
        )
      : model.defaultSortKey
        ? formatSort(model.defaultSortKey, model.defaultSortOrder)
        : ''

  const airtableName = view.airtableName
  const airtableBase = view.airtableBase

  const queryStringObj = {
    limit: maxItems,
    sort,
    view: airtableName,
    airtableBase,
  }

  const {
    isLoading,
    isFetchingNextPage,
    data,
    hasNextPage,
    isError,
    isFetched,
    fetchNextPage,
  } = useInfiniteQuery(
    ['rssArticleFilters', flattenedFilters, queryStringObj],
    async ({ pageParam: offset }) => {
      !disableTracking && trackFilters(filters, tracking)
      const query = queryBuilder({ ...queryStringObj, offset })
      const response = await axios.post(
        `${airtableBase ?? 'covid'}/rss-article-results/?${query}`,
        flattenedFilters
      )

      const data = recursiveCamelCase(response.data.results)
      const res = {
        count: response.data.count,
        results: data.map((row: any) =>
          Object.fromEntries(
            Object.entries(row).map(([key, val]) => [key, val])
          )
        ) as ICleanResponse[],
      }
      return res
    },
    {
      enabled: !!filters, // Only run the query if filters are provided
      getNextPageParam: getNextPageParam,
    }
  )
  const flattenData = flattenPaginatedData(data?.pages)?.results
  return {
    isLoading,
    data: flattenData,
    maxItems,
    canFetchMore: hasNextPage,
    fetchMore: fetchNextPage,
    isFetchingNextPage,
    isError,
    isFetched,
    total: data?.pages[0]?.count,
  }
}
